/**
 * Rich text on the page (a `text` with `runs`; docs/ops-protocol.md, "Rich
 * text"). The renderer turns each run into a template when the props arrive:
 * a `span` carrying the run's color, lines, kerning, baseline offset and link,
 * or the `image` element of an inline image. The layout pass then draws the
 * engine's lines out of them: a block per line, the height the engine gave
 * it, holding a clone of its run's span (with the run's font) per text
 * fragment and the image elements themselves.
 */

import type { RichTextLayout } from './layout';
import { bitmapLaidOut } from './bitmaps';

/** The templates of a rich text's runs, by its element. */
export const runTemplates = new WeakMap<HTMLElement, HTMLElement[]>();

/** What a text element last drew, to skip redrawing the same lines. */
const drawn = new WeakMap<HTMLElement, { templates: HTMLElement[]; key: string }>();

/** Draws the engine's lines of a rich text into its element. */
export function drawRichText(el: HTMLElement, rich: RichTextLayout): void {
  const templates = runTemplates.get(el) ?? [];
  const key = JSON.stringify([rich.lines, rich.heights, rich.fonts.map((font) => [font.css, font.monospacedDigit === true])]);
  const last = drawn.get(el);
  if (last && last.templates === templates && last.key === key) return;
  drawn.set(el, { templates, key });
  const doc = el.ownerDocument;
  const rows = rich.lines.map((line, index) => {
    const row = doc.createElement('span');
    row.className = 'sb-text-line';
    const height = `${rich.heights[index] ?? 0}px`;
    row.style.height = height;
    row.style.lineHeight = height;
    for (const fragment of line) {
      const template = templates[fragment.run];
      if (fragment.text === undefined) {
        // The image element itself: an image run is never split.
        if (!template) continue;
        const s = template.style;
        s.width = `${fragment.width}px`;
        s.height = `${fragment.height ?? 0}px`;
        // A symbol's glyph is sized by its run's font.
        const font = rich.fonts[fragment.run];
        if (font) s.fontSize = `${font.size}px`;
        row.append(template);
        bitmapLaidOut(template, fragment.width, fragment.height ?? 0);
        continue;
      }
      const span = (template?.cloneNode(false) as HTMLElement | undefined) ?? doc.createElement('span');
      const font = rich.fonts[fragment.run];
      if (font) {
        span.style.font = font.css;
        if (font.monospacedDigit) span.style.fontVariantNumeric = 'tabular-nums';
      }
      // Every fragment shares the line's baseline (its height is the tallest run's).
      span.style.lineHeight = 'inherit';
      span.textContent = fragment.text;
      row.append(span);
    }
    return row;
  });
  el.replaceChildren(...rows);
}

/** Forget what the element drew (it shows plain text now). */
export function clearRichText(el: HTMLElement): void {
  drawn.delete(el);
}
